import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, getDoc, orderBy, writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './Settlements.css';

// Advance Pay settlements — every ₹11 booking advance is collected into the Ulloor
// Styles Razorpay account, so Ulloor Styles owes each salon the advances it collected.
// This page groups paid advances (paymentTransactions, status 'paid') by salon + IST
// collection day and lets the admin mark a salon-day as paid out. The salon sees the
// result instantly in their app/web "Advance Payments" page ("Paid by Ulloor Styles
// on <date>"), so the ledger identity fields must never be touched here — Firestore
// rules enforce that only settlement/audit fields can change.

const BATCH_LIMIT = 400;

const formatDayLabel = (dayStr) => {
  if (!dayStr) return 'Unknown date';
  const [year, month, day] = String(dayStr).split('-').map(Number);
  const d = new Date(year, (month || 1) - 1, day || 1);
  if (Number.isNaN(d.getTime())) return dayStr;
  return d.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatPaidTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
};

// Only a completed service or a customer no-show is payable to the salon — the ₹11
// advance is non-refundable either way, but cancelled/expired/upcoming bookings never
// owe the salon anything.
const isPayableBookingStatus = (status) => status === 'completed' || status === 'no_show';

// One-line payout destination for a salon (profile.paymentDetails)
const formatPayoutDetails = (details) => {
  if (!details) return null;
  if (details.settlementMethod === 'bank' || (!details.upiId && details.accountNumber)) {
    const parts = [details.accountHolderName, details.accountNumber, details.ifscCode].filter(Boolean);
    return parts.length > 0 ? `Bank: ${parts.join(' · ')}` : null;
  }
  if (!details.upiId) return null;
  return `UPI: ${details.upiId}${details.accountHolderName ? ` (${details.accountHolderName})` : ''}`;
};

function Settlements() {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [filter, setFilter] = useState('pending'); // 'pending' | 'settled' | 'all'
  const [expandedGroups, setExpandedGroups] = useState({});
  const [settlingKey, setSettlingKey] = useState(null);
  // salonId -> profile.paymentDetails (settlement UPI ID / bank account), fetched from
  // the salon docs so the admin can pay each salon out without leaving this page.
  const [payoutDetailsBySalon, setPayoutDetailsBySalon] = useState({});
  // bookingReference -> bookingStatus, so admins can see whether the booking behind
  // an advance was actually completed before paying the salon out for it.
  const [bookingStatusByRef, setBookingStatusByRef] = useState({});

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // All money that actually arrived. 'created'/'expired'/'failed' rows are
      // abandoned checkouts — nothing is owed for those. refund_required/refunded
      // rows are surfaced separately below so they're never silently forgotten.
      const txnQuery = query(
        collection(db, 'paymentTransactions'),
        where('status', 'in', ['paid', 'refund_required', 'refunded']),
        orderBy('paidAt', 'desc')
      );
      const snapshot = await getDocs(txnQuery);
      const rows = [];
      snapshot.forEach((docSnap) => {
        rows.push({ id: docSnap.id, ...docSnap.data() });
      });
      setTransactions(rows);

      // Fetch the settlement payment details (UPI ID / bank account) for every salon
      // that appears in the ledger. Failures only blank the detail line, never the page.
      const salonIds = [...new Set(rows.map((t) => t.salonId).filter(Boolean))];
      const detailEntries = await Promise.all(
        salonIds.map(async (salonId) => {
          try {
            const salonSnap = await getDoc(doc(db, 'salons', salonId));
            const salonProfile = salonSnap.exists() ? salonSnap.data()?.profile : null;
            // Fall back to the legacy profile.upiId field for salons onboarded before
            // the Payment Details section existed.
            const details = salonProfile?.paymentDetails ||
              (salonProfile?.upiId ? { settlementMethod: 'upi', upiId: salonProfile.upiId } : null);
            return [salonId, details];
          } catch (err) {
            return [salonId, null];
          }
        })
      );
      setPayoutDetailsBySalon(Object.fromEntries(detailEntries));

      // Fetch the current bookingStatus for every booking referenced by these
      // transactions, so the admin can see whether a paid advance's booking was
      // actually completed (vs. cancelled / no-show) before settling it. Joined by
      // bookingReference since paymentTransactions docs don't carry a bookingId.
      const bookingRefs = [...new Set(rows.map((t) => t.bookingReference).filter(Boolean))];
      const statusEntries = [];
      for (let i = 0; i < bookingRefs.length; i += 30) {
        const chunk = bookingRefs.slice(i, i + 30);
        try {
          const bookingsQuery = query(
            collection(db, 'bookings'),
            where('bookingReference', 'in', chunk)
          );
          const bookingsSnap = await getDocs(bookingsQuery);
          bookingsSnap.forEach((docSnap) => {
            const data = docSnap.data();
            statusEntries.push([data.bookingReference, data.bookingStatus || 'unknown']);
          });
        } catch (err) {
        }
      }
      setBookingStatusByRef(Object.fromEntries(statusEntries));
    } catch (err) {
      setError('Failed to load advance payments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const paidRows = transactions.filter((t) => t.status === 'paid');
  const refundRows = transactions.filter((t) => t.status === 'refund_required' || t.status === 'refunded');

  // Group paid rows by salon + IST collection day — one settlement unit per group
  const groups = [];
  const groupByKey = {};
  for (const txn of paidRows) {
    const day = txn.paidDateLocal || (txn.paidAt ? String(txn.paidAt).split('T')[0] : 'unknown');
    const key = `${txn.salonId}__${day}`;
    if (!groupByKey[key]) {
      groupByKey[key] = {
        key,
        salonId: txn.salonId,
        salonName: txn.salonName || txn.salonId,
        day,
        rows: [],
      };
      groups.push(groupByKey[key]);
    }
    groupByKey[key].rows.push(txn);
  }
  groups.forEach((g) => {
    // "Collected" total — every advance that hit the Razorpay account that day,
    // regardless of whether the booking is payable. Purely informational.
    g.total = g.rows.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    g.payableRows = g.rows.filter((t) => isPayableBookingStatus(bookingStatusByRef[t.bookingReference] || 'unknown'));
    g.payableUnsettledRows = g.payableRows.filter((t) => t.settled !== true);
    g.payableUnsettledTotal = g.payableUnsettledRows.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    // A group is only "settled" once every payable row is settled — non-payable rows
    // never block this, since they're never meant to be paid out.
    g.allSettled = g.payableRows.length > 0 && g.payableRows.every((t) => t.settled === true);
    // e.g. every booking that salon-day was cancelled — nothing is owed at all.
    g.hasNothingPayable = g.payableRows.length === 0;
    g.settledAt = g.allSettled
      ? g.payableRows.reduce((max, t) => (t.settledAt && t.settledAt > max ? t.settledAt : max), '')
      : null;
    g.settlementRef = g.allSettled ? (g.payableRows.find((t) => t.settlementRef)?.settlementRef || '') : '';
    g.notCompletedCount = g.rows.length - g.payableRows.length;
  });
  // Newest collection day first, then salon name
  groups.sort((a, b) => (b.day !== a.day ? b.day.localeCompare(a.day) : a.salonName.localeCompare(b.salonName)));

  const visibleGroups = groups.filter((g) => {
    // Nothing payable that day → not actionable, only show it under "All".
    if (g.hasNothingPayable) return filter === 'all';
    if (filter === 'pending') return !g.allSettled;
    if (filter === 'settled') return g.allSettled;
    return true;
  });

  const pendingGroups = groups.filter((g) => !g.hasNothingPayable && !g.allSettled);
  const totalPendingAmount = pendingGroups.reduce((sum, g) => sum + g.payableUnsettledTotal, 0);
  const totalCollected = paidRows.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  const pendingSalonCount = new Set(pendingGroups.map((g) => g.salonId)).size;

  const handleMarkPaid = async (group) => {
    // Only completed/no_show bookings are payable — never bundle cancelled/expired
    // rows into a payout, even though they're unsettled too.
    const unsettledRows = group.payableUnsettledRows;
    if (unsettledRows.length === 0) return;

    const amountDue = unsettledRows.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const payoutLine = formatPayoutDetails(payoutDetailsBySalon[group.salonId]);
    const confirmed = window.confirm(
      `Mark ₹${amountDue} (${unsettledRows.length} advance${unsettledRows.length > 1 ? 's' : ''}) as PAID to "${group.salonName}" for ${formatDayLabel(group.day)}?\n\nPay to → ${payoutLine || 'No payment details on the salon profile!'}\n\nThe salon will immediately see "Paid by Ulloor Styles" in their app.`
    );
    if (!confirmed) return;

    const settlementRef = window.prompt(
      'Payment reference (UTR / UPI ref / note) — stored on every transaction for the audit trail:',
      ''
    );
    if (settlementRef === null) return; // admin cancelled the prompt

    try {
      setSettlingKey(group.key);
      const settlementId = `STL-${group.day}-${group.salonId.slice(0, 6)}-${Date.now()}`;
      const settledAt = new Date().toISOString();
      const settledBy = currentUser?.email || currentUser?.uid || 'admin';

      for (let i = 0; i < unsettledRows.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        unsettledRows.slice(i, i + BATCH_LIMIT).forEach((txn) => {
          batch.update(doc(db, 'paymentTransactions', txn.id), {
            settled: true,
            settledAt,
            settledBy,
            settlementId,
            settlementRef: settlementRef.trim() || null,
            settlementAmount: Number(txn.amount) || 11,
          });
        });
        await batch.commit();
      }

      showMessage('success', `Settled ₹${amountDue} to ${group.salonName} (${settlementId})`);
      fetchTransactions();
    } catch (err) {
      showMessage('error', 'Failed to mark as paid. Please try again.');
    } finally {
      setSettlingKey(null);
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="settlements-page">
        <div className="settlements-loading">Loading advance payments...</div>
      </div>
    );
  }

  return (
    <div className="settlements-page">
      <div className="page-header">
        <h1>Advance Pay Settlements</h1>
        <p>₹11 booking advances collected by Ulloor Styles, to be paid out to salons</p>
      </div>

      {message && <div className={`message ${message.type}`}>{message.text}</div>}
      {error && <div className="message error">{error}</div>}

      <div className="settlements-stats">
        <div className="stat-card">
          <div className="stat-value">₹{totalPendingAmount}</div>
          <div className="stat-label">Pending payout</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{pendingGroups.length}</div>
          <div className="stat-label">Pending salon-days ({pendingSalonCount} salon{pendingSalonCount !== 1 ? 's' : ''})</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">₹{totalCollected}</div>
          <div className="stat-label">Total advances collected</div>
        </div>
      </div>

      <div className="settlements-filters">
        {['pending', 'settled', 'all'].map((f) => (
          <button
            key={f}
            className={`filter-button ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'pending' ? `Pending (${pendingGroups.length})` : f === 'settled' ? 'Settled' : 'All'}
          </button>
        ))}
      </div>

      {visibleGroups.length === 0 ? (
        <div className="settlements-empty">
          {filter === 'pending'
            ? 'No pending payouts — every collected advance has been settled. 🎉'
            : 'No advance payments found.'}
        </div>
      ) : (
        visibleGroups.map((group) => (
          <div key={group.key} className="settlement-group">
            <div className="settlement-group-header" onClick={() => toggleGroup(group.key)}>
              <div className="settlement-group-info">
                <span className="settlement-salon">{group.salonName}</span>
                <span className="settlement-day">{formatDayLabel(group.day)}</span>
                <span className="settlement-count">
                  {group.rows.length} advance{group.rows.length > 1 ? 's' : ''} · ₹{group.total}
                </span>
                {group.notCompletedCount > 0 && (
                  <span className="settlement-warning">
                    ⚠ {group.notCompletedCount} booking{group.notCompletedCount > 1 ? 's' : ''} not payable
                  </span>
                )}
                <span className={`settlement-payout ${payoutDetailsBySalon[group.salonId] ? '' : 'missing'}`}>
                  {formatPayoutDetails(payoutDetailsBySalon[group.salonId]) || '⚠ No payment details on salon profile'}
                </span>
              </div>
              <div className="settlement-group-actions">
                {group.hasNothingPayable ? (
                  <span className="status-badge status-na">Nothing payable</span>
                ) : group.allSettled ? (
                  <span className="status-badge status-settled">
                    ✓ Paid{group.settledAt ? ` on ${formatDateTime(group.settledAt)}` : ''}
                    {group.settlementRef ? ` · ${group.settlementRef}` : ''}
                  </span>
                ) : (
                  <button
                    className="mark-paid-button"
                    disabled={settlingKey === group.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkPaid(group);
                    }}
                  >
                    {settlingKey === group.key ? 'Saving...' : `Mark ₹${group.payableUnsettledTotal} Paid`}
                  </button>
                )}
                <span className="expand-arrow">{expandedGroups[group.key] ? '▲' : '▼'}</span>
              </div>
            </div>

            {expandedGroups[group.key] && (
              <table className="settlement-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Booking Ref</th>
                    <th>Booking Status</th>
                    <th>Paid At</th>
                    <th>Order ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((txn) => {
                    const bookingStatus = bookingStatusByRef[txn.bookingReference] || 'unknown';
                    return (
                      <tr key={txn.id}>
                        <td>{txn.customerName || 'Customer'}</td>
                        <td>{txn.bookingReference || '-'}</td>
                        <td>
                          <span className={`status-badge status-${bookingStatus.toLowerCase()}`}>
                            {bookingStatus === 'unknown' ? 'Unknown' : bookingStatus.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>{formatPaidTime(txn.paidAt)}</td>
                        <td className="mono">{txn.orderId}</td>
                        <td>₹{txn.amount}</td>
                        <td>
                          {txn.settled === true ? (
                            <span className="status-badge status-settled">Settled</span>
                          ) : isPayableBookingStatus(bookingStatus) ? (
                            <span className="status-badge status-pending">Pending</span>
                          ) : (
                            <span className="status-badge status-na">NA</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}

      {refundRows.length > 0 && (
        <div className="refunds-section">
          <h2>⚠️ Refunds needed / issued</h2>
          <p className="refunds-hint">
            These customers paid but the slot could not be delivered. Refund the ₹11 from the
            Razorpay dashboard (Payments → Refund), then no further action is needed here.
          </p>
          <table className="settlement-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Salon</th>
                <th>Booking Ref</th>
                <th>Booking Status</th>
                <th>Paid At</th>
                <th>Payment ID</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {refundRows.map((txn) => {
                const bookingStatus = bookingStatusByRef[txn.bookingReference] || 'unknown';
                return (
                  <tr key={txn.id}>
                    <td>{txn.customerName || 'Customer'}</td>
                    <td>{txn.salonName || txn.salonId}</td>
                    <td>{txn.bookingReference || '-'}</td>
                    <td>
                      <span className={`status-badge status-${bookingStatus.toLowerCase()}`}>
                        {bookingStatus === 'unknown' ? 'Unknown' : bookingStatus.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{txn.paidAt ? `${formatDateTime(txn.paidAt)} ${formatPaidTime(txn.paidAt)}` : '-'}</td>
                    <td className="mono">{txn.paymentId || '-'}</td>
                    <td>
                      <span className={`status-badge ${txn.status === 'refunded' ? 'status-settled' : 'status-refund'}`}>
                        {txn.status === 'refunded' ? 'Refunded' : 'Refund required'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Settlements;
